import React, { useEffect, useRef, useState } from 'react';
import { criarContatoFornecedor, removerContatoFornecedor } from '../../../api/portalCotacoes/portalCotacoesApi';
import { Fornecedor } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import { formatPhoneBr, phoneDigits } from '../../../utils/phoneMask';
// Overlay compartilhado dos modais do sistema (definido no AddCarModal e usado por 16
// modais). Importado explicitamente para não depender de outro modal ter entrado no
// bundle antes deste.
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Detalhes e edição de um fornecedor — abre ao clicar no nome dele na tabela da
 * configuração de Cotações (at-73).
 *
 * Por que existe: a tabela é **somente leitura** (decisão do usuário, 11/08/2026). Ter
 * "Adicionar contato" e o "×" de remover dentro das células fazia cada linha carregar
 * ação destrutiva, e o padrão do sistema é outro — na Recepção a placa é clicável e as
 * ações vivem no modal. Aqui o nome do fornecedor é o link, como a placa lá.
 *
 * Estilo: segue a receita dos modais existentes (cabeçalho com título + botão de fechar,
 * corpo em seções com `h3` laranja e grade de rótulo/valor, fechar clicando fora),
 * espelhada de `AdministracaoActionsModal`. As classes são namespeadas neste arquivo
 * porque é assim que cada modal do projeto faz — o que se compartilha é o overlay.
 */

interface FornecedorDetalhesModalProps {
  fornecedor: Fornecedor;
  onFechar: () => void;
  /** Chamado depois de qualquer mudança que altere a lista da tela. */
  onAtualizado: () => Promise<void> | void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  /**
   * Aberto SOBRE outro modal — hoje o wizard "Nova Cotação" (at-11).
   *
   * 🔴 Existe porque `.add-car-modal-overlay` e `.pc-drawer-overlay` têm **o mesmo**
   * `z-index: 1050`, e empate não basta: o desempate vira a ordem do DOM, que ninguém
   * controla ao escrever CSS e que muda quando alguém acrescenta um componente na tela
   * (é o `BUG-R97`, que fez o "Alterar esteira" da Oficina abrir ATRÁS do modal de
   * detalhes em produção). Com a prop, quem empilha declara que empilha.
   */
  empilhado?: boolean;
}

const FornecedorDetalhesModal: React.FC<FornecedorDetalhesModalProps> = ({
  fornecedor,
  onFechar,
  onAtualizado,
  sidebarRef,
  empilhado = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [processando, setProcessando] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onFechar();
    };

    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onFechar, sidebarRef]);

  /*
   * Formato conferido AQUI, não só no backend, porque o backend recusa os dois casos com
   * 400 (`TELEFONE_INVALIDO` e "E-mail inválido.") — deixar o usuário descobrir isso
   * depois de clicar em salvar é ida e volta desnecessária.
   *
   * O telefone é mascarado enquanto se digita (`formatPhoneBr`, o mesmo utilitário do
   * cadastro de veículo) e persistido só com dígitos (`phoneDigits`), que é o formato que
   * o backend guarda — 10 a 13 dígitos, o intervalo do `normalizePhone`.
   */
  const digitosTelefone = phoneDigits(telefone);
  const telefoneIncompleto = digitosTelefone.length > 0 && digitosTelefone.length < 10;
  const emailInvalido = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  const temAlgumCanal = Boolean(email || digitosTelefone);
  const podeSalvar = temAlgumCanal && !telefoneIncompleto && !emailInvalido;

  async function salvarContato() {
    if (!podeSalvar) return;
    setProcessando(true);
    try {
      await criarContatoFornecedor(fornecedor.cnpj, {
        nome: nome.trim() || undefined,
        email: email || undefined,
        telefone: digitosTelefone || undefined,
      });
      notify.actionSuccess({ action: 'create', subject: 'Contato' });
      setNome('');
      setEmail('');
      setTelefone('');
      setFormAberto(false);
      await onAtualizado();
    } catch (error) {
      notify.actionError(error, { action: 'create', subject: 'Contato' });
    } finally {
      setProcessando(false);
    }
  }

  async function removerContato(id: string) {
    setProcessando(true);
    try {
      await removerContatoFornecedor(id);
      notify.actionSuccess({ action: 'delete', subject: 'Contato' });
      await onAtualizado();
    } catch (error) {
      notify.actionError(error, { action: 'delete', subject: 'Contato' });
    } finally {
      setProcessando(false);
    }
  }

  return (
    <div className={`add-car-modal-overlay${empilhado ? ' modal-overlay--empilhado' : ''}`}>
      <div className="fornecedor-modal-content" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>{fornecedor.razaoSocial}</h2>
            <span className="fornecedor-modal-subtitulo">{fornecedor.cnpj}</span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          <div className="fornecedor-modal-secao">
            <h3>Identidade</h3>
            <p className="fornecedor-modal-hint">
              Estes dados são espelhados do ERP pela importação — não se editam aqui.
            </p>
            <div className="fornecedor-info-grid">
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">CNPJ</span>
                <span className="fornecedor-info-value">{fornecedor.cnpj}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Código no ERP</span>
                <span className="fornecedor-info-value">{fornecedor.codigoErp ?? '-'}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Situação</span>
                <span className="fornecedor-info-value">{fornecedor.inativo ? 'Inativo' : 'Ativo'}</span>
              </div>
              <div className="fornecedor-info-item">
                <span className="fornecedor-info-label">Nota de qualidade</span>
                <span className="fornecedor-info-value">
                  {fornecedor.notaQualidade === null
                    ? 'Sem nota'
                    : `${Number(fornecedor.notaQualidade).toFixed(1)} (inativa no cálculo)`}
                </span>
              </div>
            </div>
          </div>

          <div className="fornecedor-modal-secao">
            <h3>Contatos</h3>
            <p className="fornecedor-modal-hint">
              É por estes contatos que o convite de cotação sai. O ERP não guarda contato de fornecedor, então
              esta lista é mantida aqui — fornecedor sem contato não recebe convite.
            </p>

            {fornecedor.contatos.length === 0 ? (
              <p className="fornecedor-modal-vazio">Nenhum contato cadastrado.</p>
            ) : (
              <div className="fornecedor-contatos-lista">
                {fornecedor.contatos.map((c) => (
                  <div key={c.id} className="fornecedor-contato-linha">
                    <div className="fornecedor-contato-dados">
                      <span className="fornecedor-contato-nome">{c.nome ?? 'Contato'}</span>
                      <span className="fornecedor-contato-canais">
                        {/* O telefone é guardado só com dígitos: exibe formatado. */}
                        {[c.email, c.telefone ? formatPhoneBr(c.telefone) : null]
                          .filter(Boolean)
                          .join(' · ') || 'sem e-mail e sem telefone'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="fornecedor-modal-btn fornecedor-modal-btn--perigo"
                      disabled={processando}
                      onClick={() => removerContato(c.id)}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}

            {formAberto ? (
              <div className="fornecedor-contato-form">
                <div className="fornecedor-campo">
                  <label className="fornecedor-info-label" htmlFor="fc-nome">
                    Nome
                  </label>
                  <input
                    id="fc-nome"
                    className="fornecedor-modal-input"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                  />
                </div>
                <div className="fornecedor-campo">
                  <label className="fornecedor-info-label" htmlFor="fc-email">
                    E-mail
                  </label>
                  <input
                    id="fc-email"
                    type="email"
                    inputMode="email"
                    autoComplete="off"
                    spellCheck={false}
                    className="fornecedor-modal-input"
                    value={email}
                    // Espaço não existe em e-mail e maiúscula não muda o endereço: some
                    // com os dois na digitação, em vez de recusar depois.
                    onChange={(e) => setEmail(e.target.value.replace(/\s/g, '').toLowerCase())}
                    placeholder="contato@fornecedor.com.br"
                  />
                  {emailInvalido && (
                    <span className="fornecedor-modal-erro-campo">
                      E-mail incompleto — falta o "@" ou o domínio (ex: contato@fornecedor.com.br).
                    </span>
                  )}
                </div>
                <div className="fornecedor-campo">
                  <label className="fornecedor-info-label" htmlFor="fc-telefone">
                    Telefone (WhatsApp)
                  </label>
                  <input
                    id="fc-telefone"
                    inputMode="numeric"
                    autoComplete="off"
                    className="fornecedor-modal-input"
                    value={telefone}
                    // Só dígitos entram; a máscara põe parênteses e hífen sozinha.
                    onChange={(e) => setTelefone(formatPhoneBr(e.target.value))}
                    placeholder="(31) 98765-4321"
                  />
                  {telefoneIncompleto && (
                    <span className="fornecedor-modal-erro-campo">
                      Telefone incompleto: faltam {10 - digitosTelefone.length} dígito(s) — com DDD, são 10
                      (fixo) ou 11 (celular).
                    </span>
                  )}
                </div>

                <p className="fornecedor-modal-hint">
                  Informe ao menos e-mail ou telefone — é por eles que o convite sai.
                </p>

                <div className="fornecedor-modal-acoes">
                  <button
                    type="button"
                    className="fornecedor-modal-btn"
                    disabled={processando}
                    onClick={() => {
                      setFormAberto(false);
                      setNome('');
                      setEmail('');
                      setTelefone('');
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                    disabled={!podeSalvar || processando}
                    onClick={salvarContato}
                  >
                    {processando ? 'Salvando...' : 'Salvar contato'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                onClick={() => setFormAberto(true)}
              >
                Adicionar contato
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FornecedorDetalhesModal;
